import type { LayoutsMissing } from '@/llm/planLayouts';

/**
 * What the wizard says when a model call it made did not give it anything.
 *
 * Kept apart from the screen so the sentences can be checked against the rule they broke:
 * say the button that is actually there, and do not call a permanent condition a passing one.
 */

/** The label on the layout step's only forward button. The notes name it, so they share it. */
export const BUILD_LABEL = 'Build it';

/**
 * What a visitor with no account is told about describing a look in words. The `/llm` proxy
 * refuses a caller with no session, so asking would always fail — and "try again" would send
 * them round in a circle. The three looks on screen need no account, so the sentence offers them.
 */
export const SIGN_IN_TO_DESCRIBE_A_LOOK = 'Sign in to describe a look. Or pick one of these three.';

/** A refusal that will happen again every time, until something about the ACCOUNT changes. */
export type Refusal = 'signed-out' | 'no-credits';

/**
 * Why the server refused, when it refused for a reason retrying cannot fix.
 *
 * Three clients carry the same two refusals in three error classes: the phone's `/llm` client
 * throws `GuidedLLMError('signup_required' | 'insufficient_credits')`, the phone's build
 * client throws `BuildServiceError` with the same codes as `reason`, and the browser throws an
 * `ApiError` whose `kind` is 'unauthorized' / 'out_of_credits' (and whose `reason` is the code
 * again). This reads the SHAPE rather than the class because importing the phone's two classes
 * here would drag the accounts stack into every screen and test that only wants a sentence.
 *
 * `reason` is compared as an exact code. On some routes the server puts a human sentence in
 * that field instead — a sentence can never equal 'signup_required', so it falls through to
 * null, which is the "might not repeat" answer and the safe one to be wrong with.
 *
 * Only knowing the native shape was the bug: `{kind:'unauthorized'}` and `{status:401}` are
 * the browser's, so on the phone a session that lapsed mid-wizard was told "try again".
 */
export function refusalOf(err: unknown): Refusal | null {
  const e = (err ?? {}) as { kind?: unknown; status?: unknown; reason?: unknown };
  if (e.reason === 'signup_required' || e.kind === 'unauthorized' || e.status === 401) {
    return 'signed-out';
  }
  if (e.reason === 'insufficient_credits' || e.kind === 'out_of_credits' || e.status === 402) {
    return 'no-credits';
  }
  return null;
}

/** Why a model call gave the wizard nothing: a lasting refusal, or a failure worth retrying. */
export function missingFrom(err: unknown): LayoutsMissing {
  return refusalOf(err) ?? 'failed';
}

/**
 * Said in place of the options when there are none.
 *
 * Each names the button that is actually there — the old sentence said "Tap Next", on the one
 * step whose button says Build it — and only a failure that might not repeat says so. Telling
 * a signed-out visitor "just now" about a refusal they will get every time is a small lie that
 * sends them back to try again.
 */
export function layoutsNote(missing: LayoutsMissing): string {
  switch (missing) {
    case 'signed-out':
      return 'Layout options come with an account, so this app will be laid out the way the '
        + `plan describes. The look and the name below still count — tap ${BUILD_LABEL} when they are right.`;
    case 'no-credits':
      return 'Layout options need a credit balance and this account has none left, so the app '
        + `will be laid out the way the plan describes. Tap ${BUILD_LABEL} to carry on.`;
    case 'failed':
    default:
      return 'Could not work out layouts just now — the app will be laid out the way the plan '
        + `describes. Tap ${BUILD_LABEL} to carry on, or go Back and return here to try again.`;
  }
}

/**
 * The layout step's subtitle, which has to be true for whoever is reading it.
 *
 * It said "Pick an arrangement" to everybody, and a signed-out visitor — the wizard's main
 * audience, whose layout call is never made — and anyone whose call failed had no arrangement
 * to pick. `arrangements` is whether options are on screen or on their way.
 */
export function layoutSubtitle(s: { arrangements: boolean }): string {
  return s.arrangements
    ? 'Pick an arrangement and a look, and say anything else you want.'
    : 'Pick a look, name it, and say anything else you want.';
}

/**
 * Said under the header when the plan on screen was not tailored to the idea.
 *
 * Saying nothing was tried and was wrong: the screen looked as if it had ignored the sentence
 * somebody had just typed. Saying "just now" to a signed-out visitor was wrong the other way —
 * `/plan` refuses anonymous callers, so it was never going to work, and "just now" invites
 * them to wait for something that will not come. The same holds for an account that cannot
 * pay for the call, which is refused every time until it tops up — so `failed` carries WHY,
 * and only 'failed' itself is called passing.
 */
export function tailoringNote(s: { signedIn: boolean; failed: LayoutsMissing | null }): string | null {
  if (!s.signedIn) {
    return 'This plan is drawn from your sentence as written — tailoring it further needs an '
      + 'account. It is complete: edit anything, and make your free account when you build.';
  }
  switch (s.failed) {
    case null:
      return null;
    case 'no-credits':
      return 'Tailoring needs credits this account does not have, so this plan is drawn from '
        + 'your sentence as written. It is complete — edit anything before you build.';
    case 'signed-out':
      return 'Your session has ended, so this plan is drawn from your sentence as written. It '
        + 'is complete — edit anything; building needs you signed in again.';
    case 'failed':
    default:
      return 'Couldn’t tailor it just now. This is a complete plan — edit anything, and building '
        + 'works as normal.';
  }
}
